import React, { useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { Button } from 'framework/ui/mui-builder';
import { useFormContext } from 'framework/form';
import { IssueItem, IssueStatus } from '../shared/types';
import PostponeForm from '../shared/form/PostponeForm';
import IssueItemComponent from '../shared/IssueItemComponent';

const DEFAULT_POSTPONE_OPTIONS = {
  ONE_WEEK: { label: '1 Week', value: { postponeWeeks: 1 } },
  TWO_WEEKS: { label: '2 Weeks', value: { postponeWeeks: 2 } },
  ONE_MONTH: { label: '1 Month', value: { postponeMonths: 1 } },
} as const;

interface IssuesFormContentProps {
  issues: Partial<IssueItem>[];
  setIssues: React.Dispatch<React.SetStateAction<Partial<IssueItem>[]>>;
  postponeKey?: string;
  postponeReason?: string;
  setPostponeKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPostponeReason: React.Dispatch<React.SetStateAction<string | undefined>>;
  config: any;
  createIssue: (issueData: Partial<IssueItem>, isNew?: boolean) => Partial<IssueItem>;
  onClose: () => void;
  globalData?: Record<string, unknown>;
}

const IssuesFormContent: React.FC<IssuesFormContentProps> = ({
  issues,
  setIssues,
  postponeKey,
  postponeReason,
  setPostponeKey,
  setPostponeReason,
  config,
  createIssue,
  onClose,
  globalData,
}) => {
  const formContext = useFormContext();

  const updateFormData = useCallback(
    (updatedIssues: Partial<IssueItem>[], updatedPostponeKey?: string, updatedPostponeReason?: string) => {
      if (formContext?.setFormData) {
        formContext.setFormData({
          issues: updatedIssues,
          postponeKey: updatedPostponeKey,
          postponeReason: updatedPostponeReason,
        });
      }
    },
    [formContext],
  );

  useEffect(() => {
    if (formContext?.setFormData) {
      updateFormData(issues, postponeKey, postponeReason);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (formContext?.submitForm) {
      formContext.submitForm();
    }
  };

  const addNewIssue = useCallback(() => {
    setIssues(prev => [...prev, createIssue({ description: '' }, true)]);
  }, [createIssue, setIssues]);

  const updateIssue = useCallback(
    (index: number, updates: Partial<IssueItem>) => {
      setIssues(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...updates };
        return updated;
      });
    },
    [setIssues],
  );

  const removeIssue = useCallback(
    (index: number) => {
      setIssues(prev => prev.filter((_, i) => i !== index));
    },
    [setIssues],
  );

  const handleDescriptionChange = useCallback(
    (index: number, value: string) => {
      updateIssue(index, { description: value });
    },
    [updateIssue],
  );

  const handleStatusChange = useCallback(
    (index: number, status: IssueStatus) => {
      updateIssue(index, { status });
    },
    [updateIssue],
  );

  const postponeOptions =
    config.postponeOptions ||
    Object.fromEntries(Object.entries(DEFAULT_POSTPONE_OPTIONS).map(([k, v]) => [k, v.label]));

  const hasPostponedIssues = issues.some(issue => issue.status === 'POSTPONED');
  const hasValidIssues = issues.some(issue => issue.description && issue.description.trim().length > 0);
  const isPostponeFormValid =
    !hasPostponedIssues || (postponeKey && postponeReason && postponeReason.trim().length > 0);
  const isFormValid = hasValidIssues && isPostponeFormValid;
  const isSubmitDisabled = !isFormValid || !formContext?.isSubmitEnabled;

  const currentZone = (globalData as any)?.employeeData?.retentionRiskPriority || (globalData as any)?.currentZone;
  const isDelegatedZone = currentZone === 'DELEGATED';

  return (
    <Box p={3}>
      <Typography variant="h3" mb={2}>
        Manage issues
      </Typography>

      {issues.length > 0 && (
        <Box>
          {issues.map((issue, index) => (
            <IssueItemComponent
              key={issue.id ? String(issue.id) : `${index}`}
              issue={issue}
              index={index}
              onDescriptionChange={handleDescriptionChange}
              onStatusChange={handleStatusChange}
              onRemove={removeIssue}
            />
          ))}
        </Box>
      )}

      {!isDelegatedZone && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button startIcon={<Add />} onClick={addNewIssue} variant="outlined" size="small">
            Add issue
          </Button>
        </Box>
      )}

      {hasPostponedIssues && (
        <PostponeForm
          postponeKey={postponeKey}
          postponeReason={postponeReason}
          postponeOptions={postponeOptions}
          postponeOptionErrors={[]}
          postponeReasonErrors={[]}
          onPostponeKeyChange={setPostponeKey}
          onPostponeReasonChange={setPostponeReason}
        />
      )}

      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default IssuesFormContent;
